import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { MultiImageUploader } from '../../components/ImageUploader';
import { resolveImageUrl } from '../../lib/imageUrl';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminProjects() {
  const { t, getLocalizedContent } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: { fr: '', en: '' },
    description: { fr: '', en: '' },
    category: 'ongoing',
    service_type: 'btp',
    images: [],
    client: '',
    location: '',
    year: new Date().getFullYear(),
    is_featured: false,
    is_active: true
  });
  const [imageUrl, setImageUrl] = useState('');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects?active_only=false`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/projects/${editingItem.id}`, formData);
        toast.success('Projet mis à jour');
      } else {
        await axios.post(`${API}/admin/projects`, formData);
        toast.success('Projet créé');
      }
      setShowModal(false);
      resetForm();
      fetchProjects();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce projet ?')) return;
    try {
      await axios.delete(`${API}/admin/projects/${id}`);
      toast.success('Projet supprimé');
      fetchProjects();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title || { fr: '', en: '' },
      description: item.description || { fr: '', en: '' },
      category: item.category || 'ongoing',
      service_type: item.service_type || 'btp',
      images: item.images || [],
      client: item.client || '',
      location: item.location || '',
      year: item.year || new Date().getFullYear(),
      is_featured: item.is_featured || false,
      is_active: item.is_active !== false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      title: { fr: '', en: '' },
      description: { fr: '', en: '' },
      category: 'ongoing',
      service_type: 'btp',
      images: [],
      client: '',
      location: '',
      year: new Date().getFullYear(),
      is_featured: false,
      is_active: true
    });
    setImageUrl('');
  };

  const addImage = () => {
    if (imageUrl) {
      setFormData({ ...formData, images: [...formData.images, imageUrl] });
      setImageUrl('');
    }
  };

  const removeImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  return (
    <div data-testid="admin-projects">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">
          {t('admin_projects')}
        </h1>
        <Button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white"
          data-testid="add-project-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          {t('admin_new')}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((item, index) => (
            <Card key={item.id} className="border-0 shadow-sm overflow-hidden" data-testid={`project-item-${index}`}>
              <div className="relative h-40">
                <img
                  src={resolveImageUrl(item.images?.[0]) || 'https://via.placeholder.com/400x200'}
                  alt={getLocalizedContent(item.title)}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-2 right-2 flex gap-1">
                  {item.is_featured && <Badge className="bg-[#E50E24]">Vedette</Badge>}
                  <Badge className={item.category === 'completed' ? 'bg-green-500' : 'bg-blue-500'}>
                    {item.category === 'completed' ? 'Terminé' : 'En cours'}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-4">
                <h3 className="font-semibold text-[#0F172A] mb-1">{getLocalizedContent(item.title)}</h3>
                <p className="text-sm text-gray-500">{item.location} • {item.year}</p>
                <div className="flex items-center justify-between mt-4">
                  <span className={`px-2 py-1 text-xs rounded-full ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                    {item.is_active ? 'Actif' : 'Inactif'}
                  </span>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Modifier le projet' : 'Nouveau projet'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Titre (FR) *</Label>
                <Input
                  value={formData.title.fr}
                  onChange={(e) => setFormData({ ...formData, title: { ...formData.title, fr: e.target.value } })}
                  required
                />
              </div>
              <div>
                <Label>Titre (EN)</Label>
                <Input
                  value={formData.title.en}
                  onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Description (FR) *</Label>
                <Textarea
                  value={formData.description.fr}
                  onChange={(e) => setFormData({ ...formData, description: { ...formData.description, fr: e.target.value } })}
                  rows={3}
                  required
                />
              </div>
              <div>
                <Label>Description (EN)</Label>
                <Textarea
                  value={formData.description.en}
                  onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })}
                  rows={3}
                />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Statut</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ongoing">En cours</SelectItem>
                    <SelectItem value="completed">Terminé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type de service</Label>
                <Select value={formData.service_type} onValueChange={(v) => setFormData({ ...formData, service_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="btp">BTP</SelectItem>
                    <SelectItem value="communication">Communication</SelectItem>
                    <SelectItem value="printing">Impression</SelectItem>
                    <SelectItem value="equipment">Équipement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <Label>Client</Label>
                <Input
                  value={formData.client}
                  onChange={(e) => setFormData({ ...formData, client: e.target.value })}
                />
              </div>
              <div>
                <Label>Lieu</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                />
              </div>
              <div>
                <Label>Année</Label>
                <Input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                />
              </div>
            </div>
            <div>
              <MultiImageUploader
                label="Images du projet"
                values={formData.images}
                onChange={(imgs) => setFormData({ ...formData, images: imgs })}
                testIdPrefix="project-images"
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_featured}
                  onCheckedChange={(v) => setFormData({ ...formData, is_featured: v })}
                />
                <Label>Projet vedette</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                />
                <Label>Actif</Label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                {t('common_cancel')}
              </Button>
              <Button type="submit" className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white">
                {t('common_save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
